import { Pouch, Sheet, Group } from '../types';
import {
  applyPouchScalarForSheet,
  allScalarMergeLeavesAreTimePouches,
  getLayerValue,
  getPouchLayerIsTime,
  getPouchLayerMultiplier,
  getSortedLayers,
  resolveActiveLayerId,
  setPouchLayerValue,
  sheetLayerIsTimeKind,
  syncMergedTimePouchTopLevel,
} from './LayerLogic';
import { clonePouch } from './PouchLogic';
import { createGroup, generateRoundedRectPath } from './GroupLogic';
import { Rect } from './PouchPlacementLogic';
import { roundPouchValue } from '../utils/pouchValueRounding';

export { allScalarMergeLeavesAreTimePouches } from './LayerLogic';

/** 1 個分の value / multiplier（id 未採番） */
export interface SplitPouchScalar {
  value: number;
  multiplier: number;
}

export type SplitPouchScalarsResult =
  | { ok: true; scalars: SplitPouchScalar[] }
  | { ok: false; error: string };

/** 割算マルチ（負）を除いた純マルチ値 */
export function getPureMultiplier(multiplier: number): number {
  return multiplier < 0 ? 1 / Math.abs(multiplier) : multiplier;
}

/** 純マルチをストア上の multiplier 表現に戻す（performSplitOperation と同規則） */
export function pureMultiplierToStored(pureMult: number, originalSign: boolean): number {
  if (originalSign) {
    return -Math.abs(1 / pureMult);
  }
  return pureMult;
}

function getEffectiveScalar(pouch: Pouch, sheet?: Sheet): { value: number; multiplier: number } {
  if (sheet?.layers?.length) {
    const lid = resolveActiveLayerId(sheet);
    return {
      value: getLayerValue(pouch, lid),
      multiplier: getPouchLayerMultiplier(pouch, lid),
    };
  }
  return { value: pouch.value, multiplier: pouch.multiplier };
}

/** N 個分解可否（splitPouchScalars と整合） */
export function canSplitPouchByMultiplier(pouch: Pouch, sheet?: Sheet): boolean {
  return splitPouchScalars(pouch, sheet).ok;
}

/** ドラッグはんぶんこ可否（performSplitOperation と整合） */
export function canHalfSplitPouchByMultiplier(pouch: Pouch, sheet?: Sheet): boolean {
  const { multiplier } = getEffectiveScalar(pouch, sheet);
  const pm = getPureMultiplier(multiplier);
  if (pm < 4) {
    return false;
  }
  const clonedPureMultiplier = Math.floor(pm / 2);
  const sourcePureMultiplier = pm - clonedPureMultiplier;
  const clonedDecimalPlaces = (clonedPureMultiplier.toString().split('.')[1] || '').length;
  const sourceDecimalPlaces = (sourcePureMultiplier.toString().split('.')[1] || '').length;
  return clonedDecimalPlaces < 5 && sourceDecimalPlaces < 5;
}

/** 純マルチ pm → floor(pm) 個の ×1 + 小数部があれば ×(pm - floor(pm)) 1 個
 * 例: ×4.55 → 4××1 + 1××0.55
 */
export function splitPouchScalars(source: Pouch, sheet?: Sheet): SplitPouchScalarsResult {
  const { value, multiplier } = getEffectiveScalar(source, sheet);
  const originalSign = multiplier < 0;
  const pmRaw = getPureMultiplier(multiplier);
  const pm = Math.round(pmRaw * 10000) / 10000;

  if (pm <= 1) {
    return { ok: false, error: 'pure multiplier too small' };
  }

  const floorCount = Math.floor(pm);
  const fractional = Math.round((pm - floorCount) * 10000) / 10000;
  const scalars: SplitPouchScalar[] = [];

  for (let i = 0; i < floorCount; i++) {
    scalars.push({
      value,
      multiplier: pureMultiplierToStored(1, originalSign),
    });
  }

  if (fractional > 1e-12) {
    scalars.push({
      value,
      multiplier: pureMultiplierToStored(fractional, originalSign),
    });
  }

  if (scalars.length < 2) {
    return { ok: false, error: 'split would produce fewer than 2 pouches' };
  }

  return { ok: true, scalars };
}

const SCALAR_PM_ROUND = 10000;

function roundPureMultiplier(pm: number): number {
  return Math.round(pm * SCALAR_PM_ROUND) / SCALAR_PM_ROUND;
}

function getNumberLayers(sheet?: Sheet) {
  if (!sheet?.layers?.length) return [];
  return getSortedLayers(sheet).filter(l => l.kind !== 'time');
}

/** 数量レイヤー entry が明示的に isTime のときのみ true（legacy top-level isTime は見ない） */
function pouchEntryOnLayerIsExplicitlyTime(p: Pouch, layerId: string): boolean {
  return p.layerValues?.[layerId]?.isTime === true;
}

function sheetHasNumberLayer(pouch: Pouch, sheet: Sheet): boolean {
  return getSortedLayers(sheet).some(
    l => l.kind !== 'time' && !getPouchLayerIsTime(pouch, l.id),
  );
}

/** 基準レイヤー純マルチ basisPm に対する分割片 piecePm の比例マルチ */
export function proportionalStoredMultiplier(
  sourceMult: number,
  basisPureMult: number,
  piecePureMult: number,
): number {
  const sign = sourceMult < 0;
  const nextPm = roundPureMultiplier(
    (getPureMultiplier(sourceMult) / basisPureMult) * piecePureMult,
  );
  return pureMultiplierToStored(nextPm, sign);
}

/**
 * 分解片を全レイヤーへ比例配分（基準 = 表示中レイヤーの純マルチ）。
 * clone 済み pouch に source の layerValues を元に反映する。
 */
export function applySplitPieceToPouch(
  pouch: Pouch,
  source: Pouch,
  scalar: SplitPouchScalar,
  sheet: Sheet,
): Pouch {
  const activeId = resolveActiveLayerId(sheet);
  const basisPm = roundPureMultiplier(getPureMultiplier(getPouchLayerMultiplier(source, activeId)));
  const piecePm = roundPureMultiplier(getPureMultiplier(scalar.multiplier));
  const clearExpr = { lastExpression: '', valueWhenLastExpression: undefined as number | undefined };
  const hasNumberLayer = sheetHasNumberLayer(source, sheet);

  let p: Pouch = {
    ...pouch,
    lastExpression: '',
    valueWhenLastExpression: undefined,
    multiplyWhenLastExpression: undefined,
  };

  if (hasNumberLayer) {
    const qtyMult =
      activeId && !getPouchLayerIsTime(source, activeId)
        ? scalar.multiplier
        : proportionalStoredMultiplier(source.multiplier, basisPm, piecePm);
    p = { ...p, multiplier: qtyMult };
  }

  for (const layer of getSortedLayers(sheet)) {
    const lid = layer.id;
    const value = getLayerValue(source, lid);

    if (lid === activeId) {
      if (getPouchLayerIsTime(source, lid)) {
        p = setPouchLayerValue(
          p,
          lid,
          value,
          { ...clearExpr, multiplier: scalar.multiplier },
          sheet,
        );
      } else {
        p = setPouchLayerValue(p, lid, value, clearExpr, sheet);
        if (hasNumberLayer) {
          p = { ...p, multiplier: scalar.multiplier };
        }
      }
    } else if (getPouchLayerIsTime(source, lid)) {
      const newMult = proportionalStoredMultiplier(
        getPouchLayerMultiplier(source, lid),
        basisPm,
        piecePm,
      );
      p = setPouchLayerValue(p, lid, value, { ...clearExpr, multiplier: newMult }, sheet);
    } else {
      p = setPouchLayerValue(p, lid, value, clearExpr, sheet);
    }
  }

  const activeValue = getLayerValue(p, activeId);
  const activeMult = getPouchLayerMultiplier(p, activeId);
  const isNegative =
    getPouchLayerIsTime(p, activeId) && !p.isGroupTotalPouch
      ? activeValue * activeMult < 0
      : activeValue < 0;

  return { ...p, isNegative };
}

function sumStoredMultipliers(mults: number[]): number | null {
  if (mults.some(m => m < 0) && mults.some(m => m >= 0)) return null;
  const sign = mults[0] < 0;
  const sumPm = roundPureMultiplier(mults.reduce((a, m) => a + getPureMultiplier(m), 0));
  return pureMultiplierToStored(sumPm, sign);
}

/** スカラーマージ対象の葉パウチ（GTP・mute 除く） */
export function getGroupScalarMergeLeaves(
  group: Group,
  pouches: Pouch[],
  groups: Group[],
): Pouch[] {
  const muteGroupIds = new Set(groups.filter(g => g.color === 'mute').map(g => g.id));
  return pouches.filter(
    p =>
      group.pouches?.includes(p.id) &&
      !(p as { _isDeleting?: boolean })._isDeleting &&
      !p.isGroupTotalPouch &&
      !groups.some(g => muteGroupIds.has(g.id) && g.pouches?.includes(p.id)),
  );
}

/**
 * 同一 value の葉パウチ群を 1 パウチ（value × 合計マルチ）に統合可能か。
 * 数量レイヤー（非時間）の value が一致していればよい。時間レイヤーは判定・値一致とも無視。
 * × と ÷ の混在のみ不可（合算不能）。
 */
export function canMergeGroupPouchesToScalar(leaves: Pouch[], sheet?: Sheet): boolean {
  if (leaves.length < 2) return false;

  const numberLayers = getNumberLayers(sheet);
  if (sheet?.layers?.length) {
    if (numberLayers.length > 0) {
      for (const layer of numberLayers) {
        if (leaves.some(p => pouchEntryOnLayerIsExplicitlyTime(p, layer.id))) return false;
        const vals = leaves.map(p => roundPouchValue(getLayerValue(p, layer.id)));
        const v0 = vals[0];
        if (!vals.every(v => v === v0)) return false;
      }
    }
  } else {
    const vals = leaves.map(p => roundPouchValue(p.value));
    const v0 = vals[0];
    if (!vals.every(v => v === v0)) return false;
  }

  if (sheet?.layers?.length) {
    const hasNumberLayer = sheetHasNumberLayer(leaves[0], sheet);
    if (hasNumberLayer && sumStoredMultipliers(leaves.map(l => l.multiplier)) == null) {
      return false;
    }
    for (const layer of getSortedLayers(sheet)) {
      const isTimeLayer =
        sheetLayerIsTimeKind(layer) || allLeavesAreTimeOnLayer(leaves, layer.id, sheet);
      if (!isTimeLayer) continue;
      const merged = sumStoredMultipliers(
        leaves.map(l => getPouchLayerMultiplier(l, layer.id)),
      );
      if (merged == null) return false;
    }
  } else if (sumStoredMultipliers(leaves.map(l => l.multiplier)) == null) {
    return false;
  }

  return true;
}

function allLeavesAreTimeOnLayer(leaves: Pouch[], layerId: string, sheet?: Sheet): boolean {
  return (
    leaves.length > 0 &&
    leaves.every(p => getPouchLayerIsTime(p, layerId, sheet))
  );
}

/** 葉パウチ群を 1 パウチ（value × 合計マルチ）に統合。canMerge が false なら null */
export function buildMergedScalarPouchFromLeaves(
  leaves: Pouch[],
  centerX: number,
  centerY: number,
  startZIndex: number,
  sheet?: Sheet,
): Pouch | null {
  if (!canMergeGroupPouchesToScalar(leaves, sheet)) return null;

  const template = leaves[0];
  const activeId = sheet?.layers?.length ? resolveActiveLayerId(sheet) : undefined;
  const value =
    sheet?.layers?.length && activeId
      ? roundPouchValue(getLayerValue(template, activeId))
      : roundPouchValue(template.value);
  const clearExpr = { lastExpression: '', valueWhenLastExpression: undefined as number | undefined };

  const layerMult = (p: Pouch, layerId: string) => getPouchLayerMultiplier(p, layerId);

  let p = clonePouch(template, centerX, centerY, startZIndex);
  const mergeAllTime = allScalarMergeLeavesAreTimePouches(leaves, sheet);

  if (sheet?.layers?.length) {
    const hasNumberLayer = sheetHasNumberLayer(template, sheet);

    if (hasNumberLayer) {
      const qtyMerged = sumStoredMultipliers(leaves.map(l => l.multiplier));
      if (qtyMerged == null) return null;
      p = { ...p, multiplier: qtyMerged };
    }

    for (const layer of getSortedLayers(sheet)) {
      const layerValue = roundPouchValue(getLayerValue(template, layer.id));
      const layerIsTime =
        sheetLayerIsTimeKind(layer) ||
        getPouchLayerIsTime(template, layer.id) ||
        allLeavesAreTimeOnLayer(leaves, layer.id, sheet);
      if (layerIsTime) {
        const merged = sumStoredMultipliers(leaves.map(l => layerMult(l, layer.id)));
        if (merged == null) return null;
        p = setPouchLayerValue(
          p,
          layer.id,
          layerValue,
          { ...clearExpr, multiplier: merged, isTime: true },
          sheet,
        );
      } else {
        p = setPouchLayerValue(p, layer.id, layerValue, clearExpr, sheet);
      }
    }

    if (mergeAllTime) {
      p = syncMergedTimePouchTopLevel(p, sheet);
    }

    const activeMult =
      activeId != null ? getPouchLayerMultiplier(p, activeId) : p.multiplier;
    const isNegative =
      activeId != null && getPouchLayerIsTime(p, activeId) && !p.isGroupTotalPouch
        ? value * activeMult < 0
        : value < 0;
    p = {
      ...p,
      isNegative,
      lastExpression: '',
      valueWhenLastExpression: undefined,
    };
  } else {
    const originalSign = template.multiplier < 0;
    const pms = leaves.map(l => roundPureMultiplier(getPureMultiplier(l.multiplier)));
    const sumPm = roundPureMultiplier(pms.reduce((a, b) => a + b, 0));
    const multiplier = pureMultiplierToStored(sumPm, originalSign);
    p = {
      ...p,
      value,
      multiplier,
      isNegative: value < 0,
      isTime: mergeAllTime ? true : p.isTime,
      lastExpression: '',
      valueWhenLastExpression: undefined,
    };
  }
  return p;
}

/** パウチ色 id → グループ色 id（getMatchingPouchColor の逆） */
export function getGroupColorFromPouchColor(pouchColor: string): string {
  const colorMap: Record<string, string> = {
    gray: 'gray',
    orange: 'yellow',
    'yellow-green': 'green',
    'sky-blue': 'blue',
    'red-purple': 'purple',
    pink: 'red',
  };
  return colorMap[pouchColor] || 'yellow';
}

/** split 後グループ path の外接矩形を指定中心へ平行移動（サイズは維持） */
export function centerSplitGroupBoundsAt(
  bounds: Rect,
  centerX: number,
  centerY: number
): Rect {
  const w = bounds.right - bounds.left;
  const h = bounds.bottom - bounds.top;
  return {
    left: centerX - w / 2,
    top: centerY - h / 2,
    right: centerX + w / 2,
    bottom: centerY + h / 2,
  };
}

/** split 後の新グループ（マージの逆） */
export function buildSplitGroup(
  source: Pouch,
  memberPouchIds: string[],
  splitGroupBounds: Rect,
  maxGroupZIndex: number,
  placeholderIndex: number,
  sheet?: Sheet,
  groupTitle?: string
): Group {
  const path = generateRoundedRectPath(
    splitGroupBounds.left,
    splitGroupBounds.top,
    splitGroupBounds.right,
    splitGroupBounds.bottom
  );
  const groupColor = getGroupColorFromPouchColor(source.color);
  const group = createGroup(
    path,
    memberPouchIds,
    maxGroupZIndex,
    sheet,
    groupColor,
    placeholderIndex
  );
  return {
    ...group,
    title: groupTitle ?? source.title,
    _isAdding: true,
  };
}

/** Case A（親グループ再利用）: グループ名が空なら元パウチの title を親へ継承 */
export function inheritPouchTitleToParentGroupIfEmpty(
  groups: Group[],
  parentGroupId: string | undefined,
  inheritedTitle: string | undefined,
): Group[] {
  if (!parentGroupId || !inheritedTitle?.trim()) {
    return groups;
  }
  const title = inheritedTitle.trim();
  return groups.map(g => {
    if (g.id !== parentGroupId || g.title?.trim()) {
      return g;
    }
    return { ...g, title };
  });
}

/** Case A（親グループ再利用）: 親グループ色を元パウチ色に合わせる */
export function inheritPouchColorToParentGroup(
  groups: Group[],
  parentGroupId: string | undefined,
  pouchColor: string,
): Group[] {
  if (!parentGroupId) {
    return groups;
  }
  const groupColor = getGroupColorFromPouchColor(pouchColor);
  return groups.map(g => {
    if (g.id !== parentGroupId) {
      return g;
    }
    return { ...g, color: groupColor };
  });
}

/** 配置済み座標から N 個の新規パウチを生成（元パウチの属性を引き継ぐ） */
export function buildSplitPouches(
  source: Pouch,
  scalars: SplitPouchScalar[],
  positions: { x: number; y: number }[],
  startZIndex: number,
  sheet?: Sheet
): Pouch[] {
  return scalars.map((scalar, i) => {
    let p = clonePouch(source, positions[i].x, positions[i].y, startZIndex + i);
    if (sheet?.layers?.length) {
      p = applySplitPieceToPouch(p, source, scalar, sheet);
    } else if (sheet) {
      p = applyPouchScalarForSheet(p, scalar.value, sheet, {
        multiplier: scalar.multiplier,
        clearExpression: true,
      });
    } else {
      p = {
        ...p,
        value: scalar.value,
        multiplier: scalar.multiplier,
        isNegative: scalar.value < 0,
        lastExpression: '',
        valueWhenLastExpression: undefined,
      };
    }
    return { ...p, _animateFromZero: true, title: undefined };
  });
}
